import React from "react";
import { Box } from "@mui/material";
import { Edit, Trash } from "lucide-react";
import ActionMenu from "../menus/ActionMenu";

const UserTable = ({
  users,
  loading,
  pagination,
  crudPermissions,
  onPageChange,
  onPageSizeChange,
  onEditUser,
  onDeleteUser,
  openDropdown,
  onDropdownClose,
}) => {
  if (loading) {
    return (
      <div className="users-page">
        <div className="loading-container">
          <div className="loading-spinner"></div>
          <p>Loading users...</p>
        </div>
      </div>
    );
  }

  return (
    <Box
      sx={{
        flexGrow: 1,
        overflowY: "auto",
      }}
    >
      <div
        className="users-table-container"
        style={{ overflowY: "auto", height: "calc(100vh - 370px)" }}
      >
        <table className="users-table">
          <thead>
            <tr>
              <th>Name</th>
              <th>Username</th>
              <th>Role</th>
              <th>Status</th>
              <th>Created at</th>
              <th>Action</th>
            </tr>
          </thead>
          <tbody>
            {users.map((user) => (
              <tr className="table-row" key={user.id}>
                <td>
                  <div className="user-info">
                    <span className="user-name">{user.name}</span>
                  </div>
                </td>
                <td>{user.username}</td>
                <td>
                  <div className="roles-container">
                    {user.roles && user.roles.length > 0 ? (
                      user.roles.map((role, index) => (
                        <span key={index} className="role-badge">
                          {role.name || role}
                        </span>
                      ))
                    ) : (
                      <span className="no-roles">No roles assigned</span>
                    )}
                  </div>
                </td>
                <td>
                  <div className="user-info ">
                    <div
                      className="status-badge "
                      style={{
                        backgroundColor: user.is_active ? "#4CAF50" : "#F44336",
                        borderColor: user.is_active ? "#4CAF50" : "#F44336",
                      }}
                    >
                      {user.is_active ? "active" : "inactive"}
                    </div>
                  </div>
                </td>
                <td>{new Date(user.created_at).toLocaleDateString()}</td>
                <td>
                  <div
                    className="action-dropdown"
                    onMouseLeave={() => {
                      if (openDropdown === user.id) {
                        onDropdownClose();
                      }
                    }}
                  >
                    <ActionMenu
                      actions={[
                        {
                          label: "Edit Record",
                          icon: <Edit fontSize="small" />,
                          disabled: !crudPermissions.update,
                          onClick: () => onEditUser(user),
                        },
                        {
                          label: "Delete Record",
                          icon: <Trash fontSize="small" />,
                          color: "error.main",
                          iconColor: "error.main",
                          disabled: !crudPermissions.delete,
                          onClick: () => onDeleteUser(user.id),
                        },
                      ]}
                    />
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>

        {users.length === 0 && (
          <div className="empty-state">
            <p>No users found matching your search criteria.</p>
          </div>
        )}
      </div>

      {/* Pagination */}
      <div className="pagination">
        <div className="pagination-info">
          {/* Defensive: bounds check for pagination calculations */}
          {(() => {
            const safePage = Math.max(1, pagination?.page || 1);
            const safeLimit = Math.max(1, pagination?.limit || 10);
            const safeTotal = Math.max(0, pagination?.total || 0);
            const start = Math.min((safePage - 1) * safeLimit + 1, safeTotal);
            const end = Math.min(safePage * safeLimit, safeTotal);
            return `Showing ${start} to ${end} of ${safeTotal} users`;
          })()}
        </div>
        <div className="pagination-controls">
          {/* Defensive: bounds check for page navigation */}
          <button
            className="pagination-btn"
            disabled={!pagination || pagination.page <= 1 || !pagination.pages}
            onClick={() => {
              const newPage = Math.max(1, (pagination?.page || 1) - 1);
              onPageChange(newPage);
            }}
          >
            &lt;
          </button>
          {(() => {
            // Defensive: ensure pagination object exists and has valid values
            if (!pagination || !pagination.pages || pagination.pages <= 0) {
              return null;
            }
            const safePages = Math.max(1, pagination.pages);
            const safePage = Math.max(
              1,
              Math.min(pagination.page || 1, safePages)
            );
            return Array.from({ length: Math.min(5, safePages) }, (_, i) => {
              const pageNum =
                Math.max(1, Math.min(safePages - 4, safePage - 2)) + i;
              if (pageNum > safePages || pageNum < 1) return null;
              return (
                <button
                  key={pageNum}
                  className={`pagination-btn ${
                    pagination.page === pageNum ? "active" : ""
                  }`}
                  onClick={() => onPageChange(pageNum)}
                >
                  {pageNum}
                </button>
              );
            });
          })()}
          {/* Defensive: bounds check for next page button */}
          <button
            className="pagination-btn"
            disabled={
              !pagination ||
              !pagination.pages ||
              pagination.page >= pagination.pages
            }
            onClick={() => {
              if (!pagination || !pagination.pages) return;
              const newPage = Math.min(
                pagination.pages,
                (pagination.page || 1) + 1
              );
              onPageChange(newPage);
            }}
          >
            &gt;
          </button>
        </div>
        <div className="pagination-size">
          {/* Defensive: validate page size selection */}
          <select
            value={pagination?.limit || 10}
            onChange={(e) => {
              const newLimit = parseInt(e.target.value, 10);
              if (newLimit > 0 && !isNaN(newLimit)) {
                onPageSizeChange(newLimit);
              }
            }}
            className="pagination-select"
          >
            <option value={5}>5 per page</option>
            <option value={10}>10 per page</option>
            <option value={25}>25 per page</option>
            <option value={50}>50 per page</option>
          </select>
        </div>
      </div>
    </Box>
  );
};

export default UserTable;
